/**
 * 链层契约：类型 + 费用常量（µDPB）。
 *
 * 金额口径（ARCH §8.4.3）：一律 **string 微单位**（1 DPB = 1_000_000 µ），禁止浮点。
 */

import type { Micro, Address, TxHash } from './types';

export type { Micro, Address, TxHash };

/** 1 DPB = 1_000_000 微单位。 */
export const MICRO_PER_DPB = 1_000_000n;

/** anchor 存证费：0.01 DPB。 */
export const FEE_ANCHOR_MICRO = 10_000n;
/** DID 首次注册：免费。 */
export const FEE_DID_FIRST_MICRO = 0n;
/** DID 更新：0.01 DPB。 */
export const FEE_DID_UPDATE_MICRO = 10_000n;
/** social（follow/unfollow）：0.001 DPB。 */
export const FEE_SOCIAL_MICRO = 1_000n;
/** 新手水龙头：100 DPB。 */
export const FAUCET_MICRO = 100_000_000n;
/** 中继结算单价：0.0025 DPB / MB。 */
export const RELAY_UNIT_PRICE_MICRO_PER_MB = 2500;
/** 金库补贴的免费 anchor 条数（PRD §6.1 关键触点）。 */
export const FREE_ANCHOR_QUOTA = 10;
/** 中继分账比例 —— 仅展示，服务端不做真实分账。 */
export const SPLIT_RELAY = { relay: 92, treasury: 3, burn: 5 } as const;
/** MeshLink 操作费分账：70% 燃烧 / 30% 流通池 —— 仅记账。 */
export const SPLIT_FEE = { burn: 70, flowReserve: 30 } as const;

/**
 * 付费内容分账（bps）：创作者 85% / 平台金库 15%。
 *
 * 口径来源：`docs/business/MeshLink_Business_Model.md` 收入流④「付费内容/订阅 — 平台抽成 15%」。
 * 与 `SPLIT_RELAY` / `SPLIT_FEE` 一致，此处也只在链上做「记账式」拆分，不引入新账户体系。
 */
export const SPLIT_PAID_CONTENT = { creatorBps: 8500, platformBps: 1500 } as const;

/** 付费内容最低定价：0.1 DPB（防 0 价刷凭证）。 */
export const MIN_PAID_CONTENT_MICRO = 100_000n;

/**
 * 付费内容分账计算（纯函数，链层与前端共用同一份口径）。
 *
 * 取整策略：创作者份额向下取整，**余数归平台** —— 这样 `creator + platform === amount`
 * 恒成立（微单位守恒），避免出现「少 1 µ 导致总额对不上」的静默账目缺口。
 */
export function splitPaidContent(amountMicro: bigint): { creatorMicro: bigint; platformMicro: bigint } {
  if (amountMicro <= 0n) return { creatorMicro: 0n, platformMicro: 0n };
  const creatorMicro = (amountMicro * BigInt(SPLIT_PAID_CONTENT.creatorBps)) / 10_000n;
  return { creatorMicro, platformMicro: amountMicro - creatorMicro };
}

/** 交易类型（覆盖 ARCH §4.1 与 DPChain MeshLink 模块规格书的全部操作码）。 */
export type TxType =
  | 'transfer'
  | 'faucet'
  | 'anchor'
  | 'did'
  | 'social'
  | 'channel_open'
  | 'channel_close'
  | 'metering'
  | 'relay_stake'
  | 'relay_reward'
  | 'referral'
  | 'promo_create'
  | 'promo_redeem'
  | 'behavior_reward'
  /** `content_purchase`：购买创作者付费内容（原子分账 + 购买凭证）。 */
  | 'content_purchase'
  /** 全行为挖矿：有效浏览 / 搜索 / 签到。 */
  | 'browse'
  | 'search'
  | 'checkin';

/** 交易类型中文名（钱包交易记录展示用，覆盖全部操作码）。 */
export const TX_LABEL: Record<string, string> = {
  transfer: '转账',
  faucet: '奖励领取',
  anchor: '内容存证',
  did: '身份注册',
  social: '社交行为',
  channel_open: '通道开启',
  channel_close: '通道关闭',
  metering: '流量计费',
  relay_stake: '中继质押',
  relay_reward: '中继奖励',
  referral: '推荐奖励',
  promo_create: '推广创建',
  promo_redeem: '推广核销',
  behavior_reward: '行为奖励',
  content_purchase: '内容购买',
  browse: '浏览奖励',
  search: '搜索奖励',
  checkin: '签到奖励',
};

export interface TxRecord {
  txHash: TxHash;
  type: TxType;
  from?: Address;
  /** 目标：转账为地址；browse/search 等行为为目标 cid / 哈希串。 */
  to?: Address | string;
  amountMicro?: Micro;
  feeMicro?: Micro;
  blockHeight: number;
  timestamp: number;
  status: 'pending' | 'confirmed' | 'failed';
  memo?: string;
}

export interface Channel {
  channelId: string;
  payer: Address;
  relay: string;
  balanceMicro: Micro;
  spentMicro: Micro;
  totalBytes: number;
  unitPriceMicroPerMB: number;
  maxBytes: number;
  seq: number;
  status: 'open' | 'closed';
  openedAt: number;
  closedAt?: number;
}

export interface Anchor {
  cid: string;
  author: Address;
  blockHeight: number;
  timestamp: number;
  txHash: TxHash;
  kind?: AnchorKind;
}

/** 存证类型（覆盖全行为挖矿：post/image/video/audio/live/repost/comment/topic 等）。 */
export type AnchorKind =
  | 'post'
  | 'image'
  | 'video'
  | 'audio'
  | 'live'
  | 'topic'
  | 'tombstone'
  | 'profile'
  | 'repost'
  | 'comment';

export interface DidRecord {
  address: Address;
  profileCid?: string;
  mailboxRelays: string[];
  devices: string[];
  registeredHeight: number;
  updatedHeight: number;
}

export interface StakeRecord {
  address: Address;
  peerId: string;
  amountMicro: Micro;
  status: 'staked' | 'unstaking' | 'slashed';
  since: number;
  unstakeHeight?: number;
}

export interface PromoRecord {
  promoId: string;
  merchant: Address;
  budgetMicro: Micro;
  couponValueMicro: Micro;
  maxUses: number;
  used: number;
  expiresHeight: number;
  promoterBps: number;
  createdHeight: number;
}

// ─────────────────────────────────────────────────────────────
// IChainClient —— 16 个方法的唯一契约（ARCH §4.1）
// ─────────────────────────────────────────────────────────────

export interface TransferReq {
  from: Address;
  to: Address;
  amountMicro: Micro;
  signature: string;
}

export interface AnchorReq {
  author: Address;
  cid: string;
  signature: string;
  kind?: Anchor['kind'];
}

export interface DidUpdateReq {
  address: Address;
  profileCid?: string;
  mailboxRelays?: string[];
  addDevices?: string[];
  removeDevices?: string[];
  signature: string;
}

export interface SocialReq {
  action: 'follow' | 'unfollow';
  follower: Address;
  followee: Address;
  signature: string;
}

/** 全行为挖矿：社交动作（like/collect/subscribe/share 等）统一上链请求。target 可为地址或内容 cid。 */
export type SocialAction = 'follow' | 'unfollow' | 'like' | 'collect' | 'subscribe' | 'share';
export interface SocialActReq {
  action: SocialAction;
  from: Address;
  target: string;
  signature: string;
}

/** 全行为挖矿：有效浏览上报（duration 秒 / ratio 0~1 完播比）。 */
export interface BrowseReq {
  from: Address;
  cid: string;
  duration: number;
  ratio: number;
  signature?: string;
}

/** 全行为挖矿：搜索上报（term_hash 64 位十六进制，明文不上链）。 */
export interface SearchReq {
  from: Address;
  termHash: string;
  signature?: string;
}

/** 全行为挖矿：签到（连续签到递增，7 天封顶）。 */
export interface CheckinReq {
  from: Address;
  ts?: number;
  signature?: string;
}

/** 内容上传大小配置（管理后台定义；前端发布预检与压缩收敛同源读取）。 */
export interface ContentConfig {
  maxImageBytes: number;
  maxVideoBytes: number;
  maxAudioBytes: number;
  maxOtherBytes: number;
}

/** 全平台模块开关（超管理台「模块管理」定义；生态门户导航按开关渲染）。 */
export type ModuleId =
  | 'chat'
  | 'feed'
  | 'trade'
  | 'rewards'
  | 'discover'
  | 'search'
  | 'checkin'
  | 'recommend'
  | 'onboarding'
  | 'merchant'
  | 'tasks';

export type ModuleConfig = Record<ModuleId, boolean>;

/** 全平台配置（公开只读；生态门户启动时拉取）。 */
export interface AppConfig {
  modules: ModuleConfig;
  content: ContentConfig;
}

export interface OpenChannelReq {
  payer: Address;
  relay: string;
  depositMicro: Micro;
  unitPriceMicroPerMB: number;
  maxBytes: number;
  signature: string;
}

export interface MeteringReq {
  channelId: string;
  bytes: number;
  seq: number;
  signature: string;
}

export interface RelayStakeReq {
  action: 'stake' | 'unstake' | 'slash';
  address: Address;
  amountMicro: Micro;
  peerId?: string;
  reason?: string;
  signature?: string;
}

/** 商家创建推广活动（v3.5）：预算入 PROMO_ESCROW 托管，核销时五方分账。 */
export interface PromoCreateReq {
  merchant: Address;
  budgetMicro: Micro;
  couponValueMicro: Micro;
  maxUses: number;
  expiresHeight: number;
  promoterBps?: number;
  signature: string;
}

/** 消费者核销推广券（v3.5）：五方分账 商家/推广者/返现/金库/燃烧。 */
export interface PromoRedeemReq {
  consumer: Address;
  promoId: string;
  amountMicro: Micro;
  signature: string;
}

export interface RelayRewardReq {
  epoch: string;
  rewards: Array<{ to: Address; amountMicro: Micro; proofHash: string }>;
}

export interface ReferralReq {
  from: Address;
  referrer: Address;
  signature: string;
}

export interface PromoCreateReq {
  merchant: Address;
  budgetMicro: Micro;
  couponValueMicro: Micro;
  maxUses: number;
  expiresHeight: number;
  promoterBps?: number;
  signature: string;
}

export interface PromoRedeemReq {
  consumer: Address;
  promoId: string;
  amountMicro: Micro;
  signature: string;
}

export interface BehaviorRewardReq {
  epoch: string;
  scope: 'daily' | 'quarterly';
  rewards: Array<{ to: Address; amountMicro: Micro; proofHash: string }>;
}

/** `content_purchase` 请求：买家为某条付费内容付款给创作者。 */
export interface ContentPurchaseReq {
  buyer: Address;
  seller: Address;
  /** 付费内容的密文 CID（`paid.lockedCid`），作为凭证主键 */
  contentCid: string;
  amountMicro: Micro;
  signature: string;
}

/** `content_purchase` 凭证：链上可查、跨设备可恢复的「已购证明」。 */
export interface ContentPurchaseRecord {
  txHash: TxHash;
  buyer: Address;
  seller: Address;
  contentCid: string;
  /** 买家实付总额 */
  amountMicro: Micro;
  /** 创作者实收（85%） */
  creatorMicro: Micro;
  /** 平台金库实收（15%，含取整余数） */
  platformMicro: Micro;
  blockHeight: number;
  timestamp: number;
}

export interface IChainClient {
  // —— 账户 ——
  registerAccount(address: Address, pubkeyB64: string): Promise<{ success: boolean; txHash?: TxHash }>;
  getBalance(address: Address): Promise<Micro>;
  getBlockHeight(): Promise<number>;

  // —— 资产 ——
  transfer(req: TransferReq): Promise<TxRecord>;
  faucet(address: Address, amountMicro?: Micro): Promise<{ txHash: TxHash; amountMicro: Micro }>;
  getTransactionHistory(address: Address, limit?: number): Promise<TxRecord[]>;

  // —— 内容存证 ——
  anchor(req: AnchorReq): Promise<{ txHash: TxHash; blockHeight: number; feeMicro: Micro }>;
  getAnchor(cid: string): Promise<Anchor | null>;

  // —— 创作者付费内容 ——
  /** `content_purchase`：扣款 + 85/15 原子分账 + 落购买凭证（幂等：已购不重复扣款）。 */
  purchaseContent(req: ContentPurchaseReq): Promise<ContentPurchaseRecord>;
  /** 查询某买家是否已购某内容（解锁状态的真源，可跨设备恢复）。 */
  hasPurchased(contentCid: string, buyer: Address): Promise<ContentPurchaseRecord | null>;

  // —— 社交图谱 ——
  follow(req: SocialReq): Promise<{ txHash: TxHash }>;
  unfollow(req: SocialReq): Promise<{ txHash: TxHash }>;
  getFollowing(follower: Address): Promise<Address[]>;
  getFollowerCount(followee: Address): Promise<number>;

  // —— 全行为挖矿：行为上链 ——
  /** 社交六动作统一提交（follow/unfollow/like/collect/subscribe/share），target 可为地址或 cid。 */
  socialAct(req: SocialActReq): Promise<{ txHash: TxHash }>;
  /** 推荐关系绑定（referral bind）：被推荐人绑定邀请人，一次永久，绑定即自动行为挖矿奖励。 */
  referral(req: ReferralReq): Promise<{ txHash: TxHash }>;
  /** 中继质押（商家入驻）：stake 入押 / unstake 申请解押。 */
  relayStake(req: RelayStakeReq): Promise<{ txHash: TxHash }>;
  /** 有效浏览上报（≥10s 且 ≥30% 有效观看，≥95% 完播）。 */
  browse(req: BrowseReq): Promise<{ txHash: TxHash; blockHeight?: number }>;
  /** 搜索上报（term_hash 64hex，每地址每日 10 次）。 */
  search(req: SearchReq): Promise<{ txHash: TxHash; blockHeight?: number }>;
  /** 签到（连续递增，7 天封顶）。 */
  checkin(req: CheckinReq): Promise<{ txHash: TxHash; blockHeight?: number }>;

  // —— 内容上传大小配置（管理后台定义，发布预检同源读取） ——
  getContentConfig(): Promise<ContentConfig>;
  /** 全平台配置（模块开关 + 内容上限；生态门户导航按开关渲染）。 */
  getAppConfig(): Promise<AppConfig>;

  // —— 支付通道 ——
  openChannel(req: OpenChannelReq): Promise<{ channelId: string; txHash: TxHash }>;
  reportMetering(req: MeteringReq): Promise<{ accepted: boolean; remainingMicro: Micro }>;
  closeChannel(req: { channelId: string; signature: string }): Promise<{ txHash: TxHash; refundedMicro: Micro }>;
  getChannel(channelId: string): Promise<Channel | null>;

  // —— DEX 交易（生态门户集成） ——
  dexMarkets(): Promise<DexPool[]>;
  dexCandles(pool: string, interval?: number, limit?: number): Promise<DexCandle[]>;
  dexOrders(pool: string, mine?: Address): Promise<DexOrder[]>;
  dexTrades(pool?: string, limit?: number): Promise<DexTrade[]>;
  /** 提交 DEX 交易（swap / add_liquidity / remove_liquidity / create_pool / order place|cancel）。 */
  dexSubmit(tx: Record<string, unknown>): Promise<{ id?: string; tx?: Record<string, unknown>; ts?: number; source?: string }>;

  // —— 全行为挖矿 / 生态展示 ——
  meshStats(): Promise<Record<string, unknown>>;
  myRewards(address: Address, limit?: number): Promise<BehaviorRewardEvent[]>;
  recommendedFeed(limit?: number): Promise<FeedItem[]>;

  // —— 商家推广（v3.5） ——
  promoCreate(req: PromoCreateReq): Promise<{ txHash: TxHash; promoId: string }>;
  promoRedeem(req: PromoRedeemReq): Promise<{ txHash: TxHash; splits: Record<string, string> }>;
  /** 推广活动明细（promos + 核销流水；商家中心）。 */
  promoList(): Promise<Record<string, unknown>>;
  /** 内容审核锚定列表（cid/kind/from/height/tombstone；管理台/生态共用）。 */
  listAnchors(limit?: number): Promise<Array<Record<string, unknown>>>;
}

// ─────────────────────────────────────────────────────────────
// DEX 与生态展示类型（MeshLink 门户集成）
// ─────────────────────────────────────────────────────────────

/** DEX 交易对（池子）。 */
export interface DexPool {
  pool_address: string;
  token0: string;
  token1: string;
  reserve0: number;
  reserve1: number;
  price?: number;
  lp_total?: number;
  fee_bps?: number;
  [k: string]: unknown;
}

/** K 线（candlestick）。 */
export interface DexCandle {
  time?: number;
  ts?: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
  [k: string]: unknown;
}

/** 限价订单。 */
export interface DexOrder {
  order_id: string;
  pool_address: string;
  side: 'buy' | 'sell';
  token: string;
  price: number;
  amount: number;
  filled?: number;
  status?: string;
  [k: string]: unknown;
}

/** 成交记录。 */
export interface DexTrade {
  type?: string;
  action?: string;
  amount_in?: number | null;
  amount_out?: number | Record<string, number>;
  token_in?: string;
  token_out?: string;
  amount?: number;
  shares?: number;
  token?: string;
  pool_address?: string;
  ts?: number;
  height?: number;
  [k: string]: unknown;
}

/** 行为挖矿奖励事件（auto_behavior_reward）。 */
export interface BehaviorRewardEvent {
  height: number;
  type: string;
  data?: {
    from?: string;
    kind?: string;
    points?: number;
    coef?: number;
    amount?: number;
    epoch?: string;
    [k: string]: unknown;
  };
}

/** 推荐流条目（recommend.py feed）。 */
export interface FeedItem {
  cid: string;
  author?: string;
  kind?: string;
  score?: number;
  ts?: number;
  height?: number;
  meta_hash?: string;
  [k: string]: unknown;
}

// ─────────────────────────────────────────────────────────────
// 金额工具（字符串运算，绝不浮点）
// ─────────────────────────────────────────────────────────────

/** string 微单位 → bigint。非法输入按 0 处理。 */
export function toMicro(value: string | number | bigint): bigint {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') return BigInt(Math.trunc(value));
  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) return 0n;
  return BigInt(normalized);
}

/** bigint → string 微单位。 */
export function microToString(value: bigint): Micro {
  return value.toString();
}

/**
 * DPB（人类可读的十进制串）→ 微单位 bigint。
 *
 * 与 `toMicro` 的区别：`toMicro` 是「微单位直通」（整数串原样转换、非法按 0），
 * 本函数用于**用户输入的金额**，支持最多 6 位小数（超出截断），并在格式非法时抛错，
 * 避免 `1.5` 被静默当成 `0` 这类「金额消失」事故。
 *
 * @throws RangeError 输入不是合法的非负十进制数
 */
export function dpbToMicro(dpb: string | number): bigint {
  const raw = typeof dpb === 'number' ? String(dpb) : dpb.trim();
  if (!/^\d+(\.\d+)?$/.test(raw)) throw new RangeError(`非法金额：${raw}`);
  const [intPart = '0', fracRaw = ''] = raw.split('.');
  const frac = (fracRaw + '000000').slice(0, 6);
  return BigInt(intPart) * MICRO_PER_DPB + BigInt(frac || '0');
}

/** 按字节数与单价计算中继费用（µ）：⌊bytes × 2500 / 1048576⌋。 */
export function meteringFeeMicro(bytes: number, unitPriceMicroPerMB: number = RELAY_UNIT_PRICE_MICRO_PER_MB): bigint {
  const safeBytes = Number.isFinite(bytes) && bytes > 0 ? Math.floor(bytes) : 0;
  return (BigInt(safeBytes) * BigInt(unitPriceMicroPerMB)) / 1_048_576n;
}

/** 判断某次 anchor 是否落在金库补贴配额内（前 N 条免费）。 */
export function isAnchorSubsidized(usedCount: number, quota: number = FREE_ANCHOR_QUOTA): boolean {
  return usedCount < quota;
}
